import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Check } from 'lucide-react';
import { api, ApiError, Feature } from '@/lib/api';

const defaultLimits = {
  maxUsers: 10,
  maxStorageGB: 1,
  maxAdmins: 1,
};

const limitLabels: Record<string, { label: string; description: string }> = {
  maxUsers: { label: 'Max Usuarios', description: 'Numero maximo de usuarios' },
  maxStorageGB: { label: 'Storage (GB)', description: 'Espaco de armazenamento em GB' },
  maxAdmins: { label: 'Max Admins', description: 'Numero maximo de administradores' },
};

export default function PlanNewPage() {
  const navigate = useNavigate();

  const [availableFeatures, setAvailableFeatures] = React.useState<Feature[]>([]);
  const [isLoadingFeatures, setIsLoadingFeatures] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const [formData, setFormData] = React.useState({
    name: '',
    slug: '',
    description: '',
    price: 0,
    billingCycle: 'monthly' as 'monthly' | 'yearly',
    displayOrder: 0,
    isActive: true,
    features: {} as Record<string, boolean>,
    limits: { ...defaultLimits },
    translationQuotaDefault: 1000000,
    googleTranslateApiKey: '',
  });

  React.useEffect(() => {
    const loadFeatures = async () => {
      try {
        const data = await api.getFeatures();
        setAvailableFeatures(data);
        // Inicializa features com todas desabilitadas
        const initialFeatures: Record<string, boolean> = {};
        data.forEach((f) => {
          initialFeatures[f.key] = false;
        });
        setFormData((prev) => ({ ...prev, features: initialFeatures }));
      } catch (err) {
        console.error('Erro ao carregar features:', err);
      } finally {
        setIsLoadingFeatures(false);
      }
    };
    loadFeatures();
  }, []);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: generateSlug(name),
    }));
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature as keyof typeof prev.features],
      },
    }));
  };

  const handleLimitChange = (limit: string, value: number) => {
    setFormData((prev) => ({
      ...prev,
      limits: {
        ...prev.limits,
        [limit]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await api.createPlan({
        name: formData.name,
        slug: formData.slug,
        description: formData.description || undefined,
        price: formData.price,
        billingCycle: formData.billingCycle,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
        features: formData.features,
        limits: formData.limits,
        translationQuotaDefault: formData.translationQuotaDefault,
        googleTranslateApiKey: formData.googleTranslateApiKey || undefined,
      });
      setSuccessMessage('Plano criado com sucesso!');
      setTimeout(() => {
        navigate('/plans');
      }, 1500);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao criar plano');
      setIsSaving(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/plans"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Novo Plano</h1>
          <p className="text-gray-500 mt-1">
            Crie um novo plano para a plataforma
          </p>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-4">
          <div className="flex items-center gap-3">
            <Check className="h-5 w-5 text-emerald-500 shrink-0" />
            <p className="text-emerald-700 text-sm">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Basicas</h2>
            <p className="text-sm text-gray-500 mt-1">Dados principais do plano</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome do Plano *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Ex: Professional"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Slug *</label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                  placeholder="professional"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Descricao</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Descricao do plano..."
                rows={3}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all resize-none"
              />
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Preco (R$) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
                <p className="text-xs text-gray-400">{formatPrice(formData.price)}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Ciclo de Cobranca</label>
                <select
                  value={formData.billingCycle}
                  onChange={(e) => setFormData((prev) => ({ ...prev, billingCycle: e.target.value as 'monthly' | 'yearly' }))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                >
                  <option value="monthly">Mensal</option>
                  <option value="yearly">Anual</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Ordem de Exibicao</label>
                <input
                  type="number"
                  min="0"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#0057FF] focus:ring-offset-2 ${
                  formData.isActive ? 'bg-[#0057FF]' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">Plano ativo</span>
            </div>
          </div>
        </div>

        {/* Recursos */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recursos</h2>
            <p className="text-sm text-gray-500 mt-1">Recursos inclusos no plano</p>
          </div>
          <div className="p-6">
            {isLoadingFeatures ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#0057FF]" />
                <span className="ml-2 text-sm text-gray-500">Carregando recursos...</span>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {availableFeatures.map((feature) => (
                  <button
                    key={feature.key}
                    type="button"
                    onClick={() => handleFeatureToggle(feature.key)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      formData.features[feature.key]
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className={`h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        formData.features[feature.key]
                          ? 'bg-emerald-500 border-emerald-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {formData.features[feature.key] && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{feature.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Limits */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Limites</h2>
            <p className="text-sm text-gray-500 mt-1">
              Limites de uso do plano (-1 para ilimitado)
            </p>
          </div>
          <div className="p-6">
            <div className="grid gap-6 sm:grid-cols-3">
              {Object.entries(limitLabels).map(([key, { label, description }]) => (
                <div key={key} className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{label}</label>
                  <input
                    type="number"
                    min="-1"
                    value={formData.limits[key as keyof typeof formData.limits]}
                    onChange={(e) => handleLimitChange(key, parseInt(e.target.value) || 0)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                  />
                  <p className="text-xs text-gray-400">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Translation Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Configuracoes de Traducao</h2>
            <p className="text-sm text-gray-500 mt-1">
              Parametros para o servico de traducao automatica
            </p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Cota de Traducao (caracteres/mes)</label>
                <input
                  type="number"
                  min="-1"
                  value={formData.translationQuotaDefault}
                  onChange={(e) => setFormData((prev) => ({ ...prev, translationQuotaDefault: parseInt(e.target.value) || 0 }))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
                <p className="text-xs text-gray-400">
                  Numero de caracteres por mes (-1 = ilimitado, 0 = desabilitado)
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Google Translate API Key</label>
                <input
                  type="text"
                  value={formData.googleTranslateApiKey}
                  onChange={(e) => setFormData((prev) => ({ ...prev, googleTranslateApiKey: e.target.value }))}
                  placeholder="AIzaSy..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all font-mono"
                />
                <p className="text-xs text-gray-400">
                  Chave da API do Google Translate para este plano
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/plans"
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Criando...
              </>
            ) : (
              'Criar Plano'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
